import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const cycleSteps = [
  { label: '评价诊断', icon: 'ri-stethoscope-line', color: 'primary' as const },
  { label: '证据反馈', icon: 'ri-chat-check-line', color: 'accent' as const },
  { label: '教研与培训', icon: 'ri-presentation-line', color: 'primary' as const },
  { label: '教学改进', icon: 'ri-arrow-up-circle-line', color: 'accent' as const },
  { label: '再次评价', icon: 'ri-refresh-line', color: 'primary' as const },
  { label: '趋势跟踪', icon: 'ri-line-chart-line', color: 'accent' as const },
];

const participants = ['教师', '教研组', '学校', '区域'];

export default function P79WrapUp({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />质量改进闭环
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从发现<span className="text-primary-600">问题</span>到复评<span className="text-gold-600">提升</span>的质量改进闭环
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            评价闭环的终点不是复评得分，而是确认教学行为和质量结构是否发生改善
          </p>
        </div>

        {/* Cycle Steps */}
        <div className={`max-w-3xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            {cycleSteps.map((step, i) => (
              <div
                key={step.label}
                className="flex flex-col items-center gap-2"
                style={{ transitionDelay: `${300 + i * 100}ms` }}
              >
                <div className={`w-14 h-14 rounded-2xl ${step.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                  <i className={`${step.icon} text-xl ${step.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                </div>
                <span className="text-xs font-body font-semibold text-foreground-700 whitespace-nowrap">{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Continuous Quality Improvement */}
        <div className={`max-w-md mx-auto mb-3 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="p-6 glass-gold border border-gold-200/40 rounded-2xl text-center">
            <i className="ri-loop-left-line text-3xl text-gold-600 mb-3 block" />
            <p className="font-heading text-lg font-bold text-foreground-800 mb-3">持续质量提升</p>
            {/* Participants */}
            <div className="flex flex-wrap justify-center gap-1.5">
              {participants.map((p, i) => (
                <span
                  key={p}
                  className="px-2.5 py-1 rounded-full text-xs font-body font-semibold bg-gold-100/80 text-gold-700 border border-gold-300/30"
                  style={{ transitionDelay: `${600 + i * 80}ms` }}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              最终形成<strong className="text-gold-700">"发现问题—证据反馈—培训培育—教学改进—复评提升—趋势跟踪"</strong>的质量闭环。如果问题在多门课程中反复出现，可组织跨课程培训；如果只集中在某门课程，则由相应课程教研组开展专项改进。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}